/**
 * Generic helpers for "where did the user come from" breadcrumbs.
 *
 * Interior detail pages that can be reached from several list pages root their
 * breadcrumb on the originating page. That page's URL travels in a `from` query
 * param; the detail page resolves it against a domain-specific matcher table to
 * pick the root crumb's label, and uses the `from` value itself as the root href
 * (so it returns to the exact list, filters and all). Display names come from the
 * destination page's own data, never from the URL.
 */
import { BREADCRUMB_FROM_LABEL_PARAM, BREADCRUMB_FROM_PARAM } from "@performyard/utils-isomorphic";

// The `from` writer is isomorphic so py-components' shared employee links can use it too.
export {
	BREADCRUMB_FROM_LABEL_PARAM,
	BREADCRUMB_FROM_PARAM,
	appendCurrentLocationAsFrom,
} from "@performyard/utils-isomorphic";

/** Root crumb for pages loaded in a fresh browsing context (new tab / deep link). */
export const DASHBOARD_ORIGIN = { href: "/", label: "Dashboard" } as const;

const currentPathname = (): string => (typeof window !== "undefined" ? window.location.pathname : "");

/**
 * No prior session history means a fresh tab — a `target=_blank` link or a new-tab open, not a URL
 * pasted into an existing tab. While set, `from` params are ignored and breadcrumbs anchor on
 * Dashboard. The first client-side pathname change clears it for good; query-only updates don't.
 */
let freshTabLoad = typeof window !== "undefined" && window.history.length <= 1;
let initialPathname = currentPathname();

export const isFreshTabLoad = (): boolean => {
	if (freshTabLoad && currentPathname() !== initialPathname) {
		freshTabLoad = false;
	}
	return freshTabLoad;
};

/** Test-only override: jsdom always reports `history.length === 1`, which would read as a fresh tab. */
export const setFreshTabLoadForTesting = (value: boolean): void => {
	freshTabLoad = value;
	initialPathname = currentPathname();
};

/**
 * Re-anchor a hand-built root crumb on Dashboard for a fresh-tab load. Parsers built on
 * `createOriginParser` get this for free.
 */
export const rootCrumbOrDashboard = <T extends { name: string; href: string }>(originCrumb: T): T =>
	isFreshTabLoad() ? { ...originCrumb, name: DASHBOARD_ORIGIN.label, href: DASHBOARD_ORIGIN.href } : originCrumb;

/**
 * Only honor internal absolute paths; reject protocol-relative/external URLs.
 * Backslashes count as slashes here — browsers normalize "/\evil.com" to "//evil.com".
 */
const sanitizeFrom = (value: string): string => (/^\/(?![/\\])/.test(value) ? value : "");

/** Like `getSanitizedFrom`, but ungated — for origins that must survive new-tab opens. */
export const getSanitizedFromEvenInFreshTab = (search: string): string =>
	sanitizeFrom(new URLSearchParams(search).get(BREADCRUMB_FROM_PARAM) ?? "");

export const getSanitizedFrom = (search: string): string => {
	if (isFreshTabLoad()) return "";
	return getSanitizedFromEvenInFreshTab(search);
};

/** Read the explicit root-crumb label, if the origin link supplied one. */
export const getFromLabel = (search: string): string =>
	new URLSearchParams(search).get(BREADCRUMB_FROM_LABEL_PARAM) ?? "";

/**
 * The chain of `from` values nested inside each other's queries, outermost hop first. Terminates
 * because each hop is a strict substring of the previous hop's query. Pass `evenInFreshTab` for
 * origins that must survive new-tab opens.
 */
export const nestedFromChain = (from: string, { evenInFreshTab = false } = {}): string[] => {
	const readFrom = evenInFreshTab ? getSanitizedFromEvenInFreshTab : getSanitizedFrom;
	const chain: string[] = [];
	let current = from;
	while (current) {
		const queryIdx = current.indexOf("?");
		if (queryIdx === -1) break;
		current = readFrom(current.slice(queryIdx));
		if (current) chain.push(current);
	}
	return chain;
};

/** First matcher whose `match` substring the path contains; null when the path is empty or nothing matches. */
export const matchOrigin = <T extends { match: string }>(path: string, matchers: readonly T[]): T | null =>
	(path && matchers.find(({ match }) => path.includes(match))) || null;

/**
 * Resolve the sanitized `from` and the first matcher whose `match` substring it contains.
 * Matchers must be ordered most-specific-first. Returns `matcher: null` when `from` is
 * absent/invalid or nothing matches.
 */
export const resolveOrigin = <T extends { match: string }>(
	search: string,
	matchers: readonly T[]
): { from: string; matcher: T | null } => {
	const from = getSanitizedFrom(search);
	return { from, matcher: matchOrigin(from, matchers) };
};

/** A path-substring → label rule that roots an interior page's breadcrumb on its origin. */
export type OriginMatcher = { match: string; label: string };

/** Reporting & Analytics landing page — the root crumb when an interior page was opened from a report. */
export const ANALYTICS_LANDING_HREF = "/analytics/";

const REPORT_ORIGIN_MATCH = "/analytics/report";

export type ReportOrigin = {
	/** Href back to the originating report. */
	reportHref: string;
	/** Display name of the originating report (carried as `fromLabel`). */
	reportName: string;
};

/**
 * When an interior page (a review form or a goal) was opened from a Reporting & Analytics
 * report, return the report origin so the breadcrumb can root on
 * "Reporting & Analytics > {Report Name}". Returns null when the origin is not a report.
 */
export const parseReportOrigin = (search: string): ReportOrigin | null => {
	const from = getSanitizedFrom(search);
	if (!from || !from.includes(REPORT_ORIGIN_MATCH)) return null;
	const reportName = getFromLabel(search);
	if (!reportName) return null;
	return { reportHref: from, reportName };
};

/**
 * Build a parser that roots the breadcrumb on the matched origin's label (and the `from` href),
 * falling back to a fixed default when there is no recognized `from`. Suits domains whose root
 * crumb is simply "the list the user came from, else a fixed default" (Surveys, Change Fields).
 */
export const createOriginParser =
	(matchers: readonly OriginMatcher[], defaultOrigin: { href: string; label: string }) =>
	(search: string): { from: string; label: string } => {
		if (isFreshTabLoad()) return { from: DASHBOARD_ORIGIN.href, label: DASHBOARD_ORIGIN.label };
		const { from, matcher } = resolveOrigin(search, matchers);
		if (from && matcher) return { from, label: matcher.label };
		return { from: defaultOrigin.href, label: defaultOrigin.label };
	};
